import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Plus,
  FileText,
  Briefcase,
  MapPin,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Search,
  Pencil,
  Folder,
  FolderOpen,
  FolderPlus,
  MoreVertical,
  X,
  Settings,
  Trash2,
  Copy,
  Download,
  FolderInput,
} from 'lucide-react';
import jobService, { JobWithEmployer } from '../../services/jobService';
import serviceManagementService from '../../services/serviceManagementService';
import cvTemplateService from '../../services/cvTemplateService';
import { jobFoldersApi, JobFolderWithStats } from '../../services/jobFoldersApi';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import useConfirmDialog from '../../hooks/useConfirmDialog';
import SelectAllToggle from '../Common/SelectAllToggle';
import './JobManagement.scss';

// Promotion info for a job
interface JobPromotion {
  jobId: number;
  startsAt: string;
  expiresAt: string;
}

/**
 * Compact page-button strip (1 … 4 5 [6] 7 8 … 24) commonly used in
 * paginated lists. Always shows first + last page; collapses long
 * runs around the current page with ellipsis markers.
 */
function buildPageButtons(current: number, total: number): Array<number | 'gap'> {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const pages: Array<number | 'gap'> = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) pages.push('gap');
  for (let p = start; p <= end; p++) pages.push(p);
  if (end < total - 1) pages.push('gap');
  pages.push(total);
  return pages;
}

const JobManagement: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { confirm, ConfirmDialogNode } = useConfirmDialog();
  const { user } = useAuth();
  const [jobs, setJobs] = useState<JobWithEmployer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [jobPromotions, setJobPromotions] = useState<Map<number, JobPromotion>>(new Map());
  const [selectedJobs, setSelectedJobs] = useState<number[]>([]);
  const [searchFilter, setSearchFilter] = useState('');
  const [clientFilter, setClientFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [selectedAction, setSelectedAction] = useState<string>('');
  // Client-side pagination — kicks in once an employer accumulates enough
  // postings that scrolling becomes painful. 50 cards per page mirrors the
  // soft cap the backend used to enforce implicitly.
  const PAGE_SIZE = 50;
  const [currentPage, setCurrentPage] = useState(1);
  const [categoryImages, setCategoryImages] = useState<{ [value: string]: string | null }>({});
  const isRecruitmentAgency = !!user?.is_recruitment_agency;

  // Job Folders (aplankai) state
  const [folders, setFolders] = useState<JobFolderWithStats[]>([]);
  const [selectedFolderId, setSelectedFolderId] = useState<number | null>(null);
  const [openFolderMenuId, setOpenFolderMenuId] = useState<number | null>(null);
  const [folderModal, setFolderModal] = useState<
    | { mode: 'create' }
    | { mode: 'edit'; folderId: number; initialName: string }
    | null
  >(null);
  const [folderModalName, setFolderModalName] = useState('');
  const [folderModalSubmitting, setFolderModalSubmitting] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  // When set, the move-modal acts on this single job; otherwise it acts on
  // the bulk selection. Cleared when the modal closes.
  const [moveTargetJobIds, setMoveTargetJobIds] = useState<number[] | null>(
    null,
  );
  const [moveSubmitting, setMoveSubmitting] = useState(false);
  const folderMenuRef = useRef<HTMLDivElement | null>(null);

  // Per-card gear menu (one open at a time).
  const [openJobMenuId, setOpenJobMenuId] = useState<number | null>(null);
  const jobMenuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    loadJobs();
    loadPromotedJobs();
    loadCategoryImages();
    loadFolders();
  }, []);

  // Close gear menus on outside click.
  useEffect(() => {
    if (openFolderMenuId === null) return;
    const onClick = (e: MouseEvent) => {
      if (
        folderMenuRef.current &&
        !folderMenuRef.current.contains(e.target as Node)
      ) {
        setOpenFolderMenuId(null);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [openFolderMenuId]);

  useEffect(() => {
    if (openJobMenuId === null) return;
    const onClick = (e: MouseEvent) => {
      if (
        jobMenuRef.current &&
        !jobMenuRef.current.contains(e.target as Node)
      ) {
        setOpenJobMenuId(null);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [openJobMenuId]);

  const loadJobs = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await jobService.getMyJobs();
      if (result.success && result.data) {
        const incoming = (result.data.jobs || []) as JobWithEmployer[];
        const seen = new Set<number>();
        setJobs(incoming.filter((job) => {
          if (job.id == null || seen.has(job.id)) return false;
          seen.add(job.id);
          return true;
        }));
      } else {
        setError(result.message || 'Nepavyko užkrauti darbo skelbimų');
      }
    } catch (error) {
      setError('Įvyko klaida užkraunant darbo skelbimus');
    } finally {
      setLoading(false);
    }
  };

  const loadFolders = async () => {
    try {
      const { folders: list } = await jobFoldersApi.list();
      setFolders(list);
    } catch (err) {
      console.error('Failed to load job folders:', err);
    }
  };

  const handleOpenCreateFolder = () => {
    setFolderModalName('');
    setFolderModal({ mode: 'create' });
  };

  const handleOpenEditFolder = (folder: JobFolderWithStats) => {
    setOpenFolderMenuId(null);
    setFolderModalName(folder.name);
    setFolderModal({
      mode: 'edit',
      folderId: folder.id,
      initialName: folder.name,
    });
  };

  const handleSubmitFolderModal = async () => {
    if (!folderModal) return;
    const trimmed = folderModalName.trim().replace(/\s+/g, ' ');
    if (trimmed.length < 1 || trimmed.length > 120) {
      showToast('Pavadinimas turi būti 1–120 simbolių.', 'error');
      return;
    }
    try {
      setFolderModalSubmitting(true);
      if (folderModal.mode === 'create') {
        await jobFoldersApi.create(trimmed);
        showToast('Aplankas sukurtas', 'success');
      } else {
        await jobFoldersApi.rename(folderModal.folderId, trimmed);
        showToast('Aplankas atnaujintas', 'success');
      }
      setFolderModal(null);
      await loadFolders();
    } catch (err: any) {
      showToast(err?.message || 'Nepavyko išsaugoti aplanko', 'error');
    } finally {
      setFolderModalSubmitting(false);
    }
  };

  const handleDeleteFolder = async (folder: JobFolderWithStats) => {
    setOpenFolderMenuId(null);
    const msg =
      folder.job_count > 0
        ? `Aplanke „${folder.name}" yra ${folder.job_count} skelbimų. Ištrynus aplanką skelbimai liks sąraše, tik bus atskirti nuo aplanko. Tęsti?`
        : `Ar tikrai norite ištrinti aplanką „${folder.name}"?`;
    if (!(await confirm(msg))) return;
    try {
      await jobFoldersApi.remove(folder.id);
      showToast('Aplankas ištrintas', 'success');
      if (selectedFolderId === folder.id) setSelectedFolderId(null);
      await Promise.all([loadFolders(), loadJobs()]);
    } catch (err: any) {
      showToast(err?.message || 'Nepavyko ištrinti aplanko', 'error');
    }
  };

  const handleMoveSelectedToFolder = async (
    targetFolderId: number | null,
  ) => {
    const jobIds = moveTargetJobIds ?? selectedJobs;
    if (jobIds.length === 0) return;
    const isSingle = moveTargetJobIds !== null;
    try {
      setMoveSubmitting(true);
      await jobFoldersApi.assignJobs(jobIds, targetFolderId);
      showToast(
        targetFolderId === null
          ? isSingle
            ? 'Skelbimas pašalintas iš aplanko'
            : 'Skelbimai pašalinti iš aplanko'
          : isSingle
          ? 'Skelbimas perkeltas į aplanką'
          : 'Skelbimai perkelti į aplanką',
        'success',
      );
      setShowMoveModal(false);
      setMoveTargetJobIds(null);
      if (!isSingle) {
        setSelectedJobs([]);
        setSelectedAction('');
      }
      await Promise.all([loadFolders(), loadJobs()]);
    } catch (err: any) {
      showToast(err?.message || 'Nepavyko perkelti skelbimų', 'error');
    } finally {
      setMoveSubmitting(false);
    }
  };

  const closeMoveModal = () => {
    setShowMoveModal(false);
    setMoveTargetJobIds(null);
  };

  const handleOpenJobMenu = (jobId: number) => {
    setOpenJobMenuId(prev => (prev === jobId ? null : jobId));
  };

  const handleDuplicateJob = async (jobId: number) => {
    setOpenJobMenuId(null);
    try {
      const result = await jobService.duplicateJob(jobId);
      if (result.success) {
        showToast('Skelbimas nukopijuotas', 'success');
        loadJobs();
      } else {
        showToast(result.message || 'Nepavyko nukopijuoti skelbimo', 'error');
      }
    } catch {
      showToast('Įvyko klaida kopijuojant skelbimą', 'error');
    }
  };

  const handleDownloadJobPdf = async (jobId: number, title?: string) => {
    setOpenJobMenuId(null);
    const ok = await jobService.downloadJobPdf(jobId, title);
    if (!ok) {
      showToast('Nepavyko atsisiųsti PDF', 'error');
    }
  };

  const handleMoveSingleToFolder = (jobId: number) => {
    setOpenJobMenuId(null);
    setMoveTargetJobIds([jobId]);
    setShowMoveModal(true);
  };

  const handleDeleteSingleJob = async (jobId: number, title?: string) => {
    setOpenJobMenuId(null);
    const label = title ? `„${title}"` : 'šį skelbimą';
    if (!(await confirm(`Ar tikrai norite ištrinti ${label}?`))) return;
    try {
      const result = await jobService.deleteJob(jobId);
      if (result.success) {
        showToast('Skelbimas ištrintas', 'success');
        setSelectedJobs(prev => prev.filter(id => id !== jobId));
        loadJobs();
      } else {
        showToast(result.message || 'Nepavyko ištrinti skelbimo', 'error');
      }
    } catch {
      showToast('Įvyko klaida trinant skelbimą', 'error');
    }
  };

  const loadPromotedJobs = async () => {
    try {
      const purchases = await serviceManagementService.getActivePurchases();
      const promotionsMap = new Map<number, JobPromotion>();
      
      purchases
        .filter(p => p.service_type === 'job_promotion' && p.job_id && p.expires_at)
        .forEach(p => {
          promotionsMap.set(p.job_id!, {
            jobId: p.job_id!,
            startsAt: p.starts_at || p.created_at,
            expiresAt: p.expires_at!
          });
        });
      
      setJobPromotions(promotionsMap);
    } catch (error) {
      console.error('Error loading promoted jobs:', error);
    }
  };

  const handleCreateJob = () => {
    navigate('/jobs/create');
  };

  const handleViewApplications = (jobId: number) => {
    navigate(`/jobs/${jobId}/applications`);
  };

  const handleEditJob = (jobId: number) => {
    navigate(`/jobs/edit/${jobId}?mode=employer`);
  };

  const handlePublishJob = async (jobId: number) => {
    try {
      const result = await jobService.publishJob(jobId);
      if (result.success) {
        loadJobs();
      } else {
        showToast(result.message || 'Nepavyko publikuoti darbo skelbimo', 'error');
      }
    } catch (error) {
      showToast('Įvyko klaida publikuojant darbo skelbimą', 'error');
    }
  };

  const handleUnpublishJob = async (jobId: number) => {
    try {
      const result = await jobService.unpublishJob(jobId);
      if (result.success) {
        loadJobs();
      } else {
        showToast(result.message || 'Nepavyko išjungti skelbimo', 'error');
      }
    } catch (error) {
      showToast('Įvyko klaida išjungiant skelbimą', 'error');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('lt-LT', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const loadCategoryImages = async () => {
    try {
      const [activityImages, workFieldImages] = await Promise.all([
        cvTemplateService.getCategoryImages('company_activity'),
        cvTemplateService.getCategoryImages('work_field')
      ]);
      // Work-field (Darbo sritis) images take precedence over company-activity.
      setCategoryImages({ ...activityImages, ...workFieldImages });
    } catch (error) {
      console.error('Error loading category images:', error);
    }
  };

  const DEFAULT_BG = 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=600&h=400&fit=crop';

  const getCategoryBackgroundImage = (job: JobWithEmployer): string => {
    const activity = job.employer_company_activity || job.company_activity;
    const workField = job.work_field;

    const imageUrl = (workField && categoryImages[workField])
      || (activity && categoryImages[activity])
      || null;

    if (imageUrl) {
      if (imageUrl.startsWith('http')) return imageUrl;
      const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '');
      return `${baseUrl}${imageUrl}`;
    }

    return DEFAULT_BG;
  };

  const getCompanyLogoUrl = (filename?: string): string | null => {
    if (!filename) return null;
    const API_BASE_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';
    return `${API_BASE_URL}/uploads/profiles/${filename}`;
  };

  const uniqueClientNames = React.useMemo(() => {
    const names = new Set<string>();
    jobs.forEach(j => { if ((j as any).client_name) names.add((j as any).client_name); });
    return Array.from(names).sort();
  }, [jobs]);

  const filterJobs = (jobsList: JobWithEmployer[]) => {
    let filtered = jobsList;
    if (selectedFolderId !== null) {
      filtered = filtered.filter(
        job => Number((job as any).folder_id) === selectedFolderId,
      );
    }
    if (searchFilter.trim()) {
      const search = searchFilter.toLowerCase();
      filtered = filtered.filter(job =>
        job.title.toLowerCase().includes(search) ||
        job.company_name?.toLowerCase().includes(search)
      );
    }
    if (clientFilter) {
      filtered = filtered.filter(job => (job as any).client_name === clientFilter);
    }
    if (statusFilter === 'expired') {
      const now = new Date();
      filtered = filtered.filter(job => (job as any).expires_at && new Date((job as any).expires_at) < now);
    } else if (statusFilter) {
      filtered = filtered.filter(job => job.status === statusFilter);
    }
    return filtered;
  };

  const handleToggleSelectAll = () => {
    const filteredJobs = filterJobs(jobs);
    if (selectedJobs.length === filteredJobs.length && filteredJobs.length > 0) {
      setSelectedJobs([]);
    } else {
      setSelectedJobs(filteredJobs.map(j => j.id!));
    }
  };

  const handleToggleSelect = (id: number) => {
    setSelectedJobs(prev => 
      prev.includes(id) ? prev.filter(jid => jid !== id) : [...prev, id]
    );
  };

  const handleDeleteSelected = async () => {
    if (selectedJobs.length === 0) return;
    
    if (!await confirm(`Ar tikrai norite ištrinti ${selectedJobs.length} skelbimų?`)) return;

    try {
      await Promise.all(selectedJobs.map(id => jobService.deleteJob(id)));
      setSelectedJobs([]);
      loadJobs();
    } catch (error) {
      showToast('Įvyko klaida trinant skelbimus', 'error');
    }
  };

  const handleBulkAction = async (action: string = selectedAction) => {
    if (!action || selectedJobs.length === 0) return;

    try {
      if (action === 'publish') {
        await Promise.all(selectedJobs.map(id => jobService.publishJob(id)));
        showToast('Skelbimai publikuoti', 'success');
      } else if (action === 'delete') {
        await handleDeleteSelected();
        setSelectedAction('');
        return;
      } else if (action === 'move-to-folder') {
        // Hand off to the folder picker; reset state happens after assign.
        setShowMoveModal(true);
        return;
      }
      setSelectedJobs([]);
      setSelectedAction('');
      loadJobs();
    } catch (error) {
      showToast('Įvyko klaida atliekant veiksmą', 'error');
    }
  };

  const filteredJobs = filterJobs(jobs);
  const totalPages = Math.max(1, Math.ceil(filteredJobs.length / PAGE_SIZE));
  // Clamp current page when the filtered set shrinks below it.
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const pageStart = (safeCurrentPage - 1) * PAGE_SIZE;
  const paginatedJobs = filteredJobs.slice(pageStart, pageStart + PAGE_SIZE);
  const showPager = filteredJobs.length > PAGE_SIZE;

  // Reset to page 1 whenever the filter inputs narrow or widen the set.
  // Using a primitive deps key keeps this effect cheap.
  useEffect(() => {
    setCurrentPage(1);
  }, [searchFilter, clientFilter, statusFilter, selectedFolderId]);

  return (
    <div className="job-management">
      {ConfirmDialogNode}
      <div className="container">
        <div className="header-row">
          <div className="breadcrumbs">
            <Link to="/">Titulinis</Link>
            <ChevronRight size={14} />
            <span>Skelbimai</span>
          </div>
          <div className="filters-toolbar">
            <SelectAllToggle
              selectedCount={selectedJobs.length}
              totalCount={filteredJobs.length}
              onToggle={handleToggleSelectAll}
            />
            <div className="action-dropdown">
              <select 
                value={selectedAction}
                onChange={(e) => {
                  const action = e.target.value;
                  setSelectedAction(action);
                  // Run the chosen action straight away — no separate "Vykdyti" step.
                  if (action && selectedJobs.length > 0) {
                    handleBulkAction(action);
                  }
                }}
                className="action-select"
              >
                <option value="">Pasirinkti veiksmą</option>
                <option value="publish">Publikuoti</option>
                <option value="delete">Ištrinti</option>
                <option value="move-to-folder">Perkelti į aplanką</option>
              </select>
            </div>
            <select
              className="status-filter-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">Visi skelbimai</option>
              <option value="published">Aktyvūs</option>
              <option value="pending">Laukia patvirtinimo</option>
              <option value="draft">Juodraštis</option>
              <option value="declined">Atmesti</option>
              <option value="expired">Nebegaliojantys</option>
            </select>
            <div className="search-input-wrapper">
              <input 
                type="text"
                className="search-title-input"
                placeholder="Skelbimo pavadinimas"
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
              />
              <Search size={16} className="search-icon" />
            </div>
            {isRecruitmentAgency && uniqueClientNames.length > 0 && (
              <select
                className="client-filter-select"
                value={clientFilter}
                onChange={(e) => setClientFilter(e.target.value)}
              >
                <option value="">Visi klientai</option>
                {uniqueClientNames.map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            )}
          </div>
        </div>

        <div className="folder-strip">
            <button
              type="button"
              className={`folder-pill ${selectedFolderId === null ? 'is-active' : ''}`}
              onClick={() => setSelectedFolderId(null)}
            >
              <FolderOpen size={14} />
              Visi skelbimai
            </button>

            {folders.map(folder => {
              const isActive = selectedFolderId === folder.id;
              const isMenuOpen = openFolderMenuId === folder.id;
              return (
                <div
                  key={folder.id}
                  className={`folder-card ${isActive ? 'is-active' : ''}`}
                >
                  <button
                    type="button"
                    className="folder-card__body"
                    onClick={() => setSelectedFolderId(folder.id)}
                    title={`Atidaryti aplanką „${folder.name}"`}
                  >
                    <span className="folder-card__icon">
                      <Folder size={28} />
                    </span>
                    <span className="folder-card__name" title={folder.name}>
                      {folder.name}
                    </span>
                    <span className="folder-card__stats">
                      <span>
                        Publikuojami{' '}
                        <strong>{folder.published_count}</strong>
                      </span>
                      <span>
                        Nepublikuojami{' '}
                        <strong>{folder.not_published_count}</strong>
                      </span>
                      <span>
                        Iš viso <strong>{folder.job_count}</strong>
                      </span>
                    </span>
                  </button>

                  <div
                    className="folder-card__menu"
                    ref={isMenuOpen ? folderMenuRef : null}
                  >
                    <button
                      type="button"
                      className="folder-card__menu-btn"
                      onClick={() =>
                        setOpenFolderMenuId(isMenuOpen ? null : folder.id)
                      }
                      aria-label="Aplanko veiksmai"
                    >
                      <MoreVertical size={16} />
                    </button>
                    {isMenuOpen && (
                      <div className="folder-card__menu-dropdown">
                        <button
                          type="button"
                          onClick={() => handleOpenEditFolder(folder)}
                        >
                          <Pencil size={14} /> Pervadinti
                        </button>
                        <button
                          type="button"
                          className="is-danger"
                          onClick={() => handleDeleteFolder(folder)}
                        >
                          <X size={14} /> Ištrinti
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            <button
              type="button"
              className="btn-new-folder"
              onClick={handleOpenCreateFolder}
              title="Sukurti naują skelbimų aplanką"
            >
              <FolderPlus size={16} />
              <span>Naujas aplankas</span>
            </button>
          </div>

        {selectedFolderId !== null && (
          <div className="folder-active-banner">
            <FolderOpen size={16} />
            <span>
              Rodomas aplankas:{' '}
              <strong>
                {folders.find(f => f.id === selectedFolderId)?.name || '—'}
              </strong>
            </span>
            <button
              type="button"
              className="folder-active-banner__clear"
              onClick={() => setSelectedFolderId(null)}
            >
              <X size={14} /> Rodyti visus skelbimus
            </button>
          </div>
        )}

        {error && (
          <div className="alert alert--error">
            {error}
          </div>
        )}

        {loading ? (
          <div className="loading">
            Kraunami darbo skelbimai...
          </div>
        ) : filteredJobs.length === 0 && jobs.length === 0 ? (
          <div className="empty-state">
            <FileText size={48} />
            <h3>Jūs dar neturite darbo skelbimų</h3>
            <p>Sukurkite savo pirmąjį darbo skelbimą ir pradėkite ieškoti talentų!</p>
            <button
              className="btn btn--primary"
              onClick={handleCreateJob}
            >
              <Plus size={20} />
              Sukurti pirmąjį skelbimą
            </button>
          </div>
        ) : (
          <div className="jobs-grid">
          {paginatedJobs.map((job) => {
            const backgroundImage = getCategoryBackgroundImage(job);
            const logoUrl = getCompanyLogoUrl(job.company_logo);
            const isPromoted = jobPromotions.has(job.id!);
            
              return (
              <div key={job.id} className={`job-card-wrapper ${isPromoted ? 'has-badge' : ''}`}>
                {/* TOP Badge - outside card for proper positioning */}
                {isPromoted && (
                  <div className="top-badge">TOP</div>
                )}
                <div className={`job-card ${selectedJobs.includes(job.id!) ? 'selected' : ''} ${isPromoted ? 'promoted' : ''}`}>
                  {/* Checkbox */}
                  <div className="job-card__checkbox">
                    <input
                      type="checkbox"
                      checked={selectedJobs.includes(job.id!)}
                      onChange={() => handleToggleSelect(job.id!)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>

                  {/* Top-right action icons: settings menu + delete */}
                  <div
                    className="job-card__actions"
                    ref={openJobMenuId === job.id ? jobMenuRef : null}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      type="button"
                      className={`job-card__action-icon ${
                        openJobMenuId === job.id ? 'is-active' : ''
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenJobMenu(job.id!);
                      }}
                      aria-label="Skelbimo nustatymai"
                      title="Nustatymai"
                    >
                      <Settings size={16} />
                    </button>
                    <button
                      type="button"
                      className="job-card__action-icon job-card__action-icon--danger"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteSingleJob(job.id!, job.title);
                      }}
                      aria-label="Ištrinti skelbimą"
                      title="Ištrinti"
                    >
                      <Trash2 size={16} />
                    </button>

                    {openJobMenuId === job.id && (
                      <div className="job-card__menu">
                        <button
                          type="button"
                          onClick={() => {
                            setOpenJobMenuId(null);
                            handleEditJob(job.id!);
                          }}
                        >
                          <Pencil size={14} /> Redaguoti
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDuplicateJob(job.id!)}
                        >
                          <Copy size={14} /> Kopijuoti
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            handleDownloadJobPdf(job.id!, job.title)
                          }
                        >
                          <Download size={14} /> Atsisiųsti
                        </button>
                        <button
                          type="button"
                          onClick={() => handleMoveSingleToFolder(job.id!)}
                        >
                          <FolderInput size={14} /> Perkelti į aplanką
                        </button>
                      </div>
                    )}
                  </div>

                {/* Background Image with Logo - Clickable to edit */}
                <div 
                  className="job-card__header"
                  style={{ backgroundImage: `url(${backgroundImage})` }}
                  onClick={() => handleEditJob(job.id!)}
                >
                  <div className="job-card__logo-container">
                    {logoUrl ? (
                      <img src={logoUrl} alt={job.company_name || 'Company logo'} className="job-card__logo" />
                    ) : (
                      <div className="job-card__logo-placeholder">
                        <Briefcase size={20} />
                      </div>
                    )}
                  </div>
                </div>

                {/* Job Title - Clickable to edit */}
                <div className="job-card__body" onClick={() => handleEditJob(job.id!)}>
                  <h3 className="job-title">{job.title}</h3>
                  <span className="edit-hint"><Pencil size={12} /> Redaguoti</span>
                </div>
                {(job as any).client_name && (
                  <div className="job-card__client-tag">
                    <span className="client-tag">{(job as any).client_name}</span>
                  </div>
                )}

                {/* Publication Period */}
                <div className="job-card__publication">
                  {job.status === 'draft' ? (
                    <div className="publication-info">
                      <span className="draft-badge">JUODRAŠTIS</span>
                      <span className="publication-date" style={{ color: '#aaa', fontSize: '12px' }}>
                        Nepublikuotas
                      </span>
                    </div>
                  ) : job.status === 'published' ? (
                    <div className="publication-info">
                      <span className="publication-label">Publikuojamas:</span>
                      <span className="publication-date">
                        nuo {formatDate(job.published_at!)}
                        {(job as any).expires_at && ` iki ${formatDate((job as any).expires_at)}`}
                      </span>
                      {(() => {
                        const exp = (job as any).expires_at;
                        if (!exp) return null;
                        const daysLeft = Math.ceil((new Date(exp).getTime() - Date.now()) / 86400000);
                        if (daysLeft < 0) return null;
                        return (
                          <span className={`days-left${daysLeft <= 7 ? ' days-left--urgent' : ''}`}>
                            {daysLeft === 0 ? 'Liko paskutinė diena' : `Liko ${daysLeft} d.`}
                          </span>
                        );
                      })()}
                    </div>
                  ) : (
                    <div className="publication-info">
                      <span className="publication-date" style={{ color: '#aaa', fontSize: '12px' }}>
                        Nepublikuota
                      </span>
                    </div>
                  )}
                  {(job as any).expires_at && new Date((job as any).expires_at) < new Date() && (
                    <span className="expired-badge">NEBEGALIOJA</span>
                  )}
                  {job.status === 'pending' ? (
                    <span className="pending-badge">LAUKIA PATVIRTINIMO</span>
                  ) : job.status === 'declined' ? (
                    <>
                      <span className="declined-badge">ATMESTA</span>
                      <button
                        className="btn-toggle"
                        onClick={() => handlePublishJob(job.id!)}
                      >
                        PATEIKTI IŠ NAUJO
                      </button>
                    </>
                  ) : job.status === 'published' ? (
                    ((job as any).expires_at && new Date((job as any).expires_at) < new Date()) ? (
                      <button
                        className="btn-toggle"
                        onClick={() => handlePublishJob(job.id!)}
                      >
                        ĮJUNGTI PUBLIKAVIMĄ
                      </button>
                    ) : (
                      <button
                        className="btn-toggle"
                        onClick={() => handleUnpublishJob(job.id!)}
                      >
                        IŠJUNGTI PUBLIKAVIMĄ
                      </button>
                    )
                  ) : (
                    <button
                      className="btn-toggle"
                      onClick={() => handlePublishJob(job.id!)}
                    >
                      ĮJUNGTI PUBLIKAVIMĄ
                    </button>
                  )}
                </div>

                {/* Promotion Status Row - Darbo skelbimo iškėlimas */}
                <div className="job-card__promotion-row">
                  <div className="promotion-info">
                    <span className="promotion-label">{jobPromotions.has(job.id!) ? 'Iškeltas iki:' : 'Neiškeltas'}</span>
                    {jobPromotions.has(job.id!) && (
                      <span className="promotion-date">{formatDate(jobPromotions.get(job.id!)!.expiresAt)}</span>
                    )}
                  </div>
                  <button
                    className={`btn-promote ${jobPromotions.has(job.id!) ? 'active' : ''}`}
                    onClick={() => navigate('/employer/services?mode=employer')}
                  >
                    {jobPromotions.has(job.id!) ? 'PRATĘSTI' : 'IŠKELTI'}
                  </button>
                </div>

                {/* Location and Salary */}
                <div className="job-card__location-salary">
                  <div className="location">
                    <MapPin size={14} />
                    {job.location}
                  </div>
                  <div className="salary">
                    <span className="salary-label">Atlyginimas</span>
                    <span className="salary-amount">{jobService.formatSalary(job.salary_min, job.salary_max, job.salary_currency)}</span>
                  </div>
                </div>

                {/* Main Action Button - EXACT COPY of cv-card-preview */}
                <button
                  className="job-card__action-button"
                  onClick={() => handleViewApplications(job.id!)}
                >
                  Kandidatai
                  <ArrowRight size={20} />
                </button>
              </div>
              </div>
            );
          })}
          </div>
        )}

        {showPager && !loading && (
          <nav className="jobs-pager" aria-label="Skelbimų puslapiai">
            <span className="jobs-pager__summary">
              Rodoma {pageStart + 1}–{Math.min(pageStart + PAGE_SIZE, filteredJobs.length)} iš {filteredJobs.length}
            </span>
            <div className="jobs-pager__controls">
              <button
                type="button"
                className="jobs-pager__btn"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={safeCurrentPage <= 1}
                aria-label="Ankstesnis puslapis"
              >
                <ChevronLeft size={16} /> Atgal
              </button>
              {buildPageButtons(safeCurrentPage, totalPages).map((entry, idx) =>
                entry === 'gap' ? (
                  <span key={`gap-${idx}`} className="jobs-pager__gap">…</span>
                ) : (
                  <button
                    key={entry}
                    type="button"
                    className={`jobs-pager__btn jobs-pager__btn--page${entry === safeCurrentPage ? ' is-current' : ''}`}
                    onClick={() => setCurrentPage(entry)}
                    aria-current={entry === safeCurrentPage ? 'page' : undefined}
                  >
                    {entry}
                  </button>
                ),
              )}
              <button
                type="button"
                className="jobs-pager__btn"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={safeCurrentPage >= totalPages}
                aria-label="Kitas puslapis"
              >
                Pirmyn <ChevronRight size={16} />
              </button>
            </div>
          </nav>
        )}
      </div>

      {folderModal && (
        <div
          className="folder-modal-overlay"
          onClick={() => !folderModalSubmitting && setFolderModal(null)}
        >
          <div
            className="folder-modal"
            onClick={e => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="folder-modal__header">
              <h3>
                {folderModal.mode === 'create'
                  ? 'Naujas aplankas'
                  : 'Pervadinti aplanką'}
              </h3>
              <button
                type="button"
                className="folder-modal__close"
                onClick={() => setFolderModal(null)}
                aria-label="Uždaryti"
              >
                <X size={18} />
              </button>
            </div>
            <div className="folder-modal__body">
              <label className="folder-modal__label">
                Pavadinimas
                <input
                  type="text"
                  value={folderModalName}
                  onChange={e => setFolderModalName(e.target.value)}
                  placeholder="Pvz., Vilniaus biuras"
                  maxLength={120}
                  autoFocus
                  onKeyDown={e => {
                    if (e.key === 'Enter') handleSubmitFolderModal();
                  }}
                />
              </label>
              <p className="folder-modal__hint">
                {folderModalName.length}/120 simbolių
              </p>
            </div>
            <div className="folder-modal__footer">
              <button
                type="button"
                className="btn btn--secondary"
                onClick={() => setFolderModal(null)}
                disabled={folderModalSubmitting}
              >
                Atšaukti
              </button>
              <button
                type="button"
                className="btn btn--primary"
                onClick={handleSubmitFolderModal}
                disabled={folderModalSubmitting || !folderModalName.trim()}
              >
                {folderModalSubmitting
                  ? 'Saugoma...'
                  : folderModal.mode === 'create'
                  ? 'Sukurti'
                  : 'Išsaugoti'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showMoveModal && (
        <div
          className="folder-modal-overlay"
          onClick={() => !moveSubmitting && closeMoveModal()}
        >
          <div
            className="folder-modal"
            onClick={e => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="folder-modal__header">
              <h3>Perkelti į aplanką</h3>
              <button
                type="button"
                className="folder-modal__close"
                onClick={closeMoveModal}
                aria-label="Uždaryti"
              >
                <X size={18} />
              </button>
            </div>
            <div className="folder-modal__body">
              <p className="folder-modal__hint">
                {(moveTargetJobIds?.length ?? selectedJobs.length) === 1
                  ? 'Pasirinktas skelbimas bus perkeltas į pasirinktą aplanką.'
                  : `Pasirinkti ${
                      moveTargetJobIds?.length ?? selectedJobs.length
                    } skelbimų bus perkelti į pasirinktą aplanką.`}
              </p>
              <div className="folder-pick-list">
                {folders.length === 0 ? (
                  <div className="folder-pick-empty">
                    Dar neturite aplankų. Sukurkite pirmąjį naudodami
                    mygtuką „Naujas aplankas".
                  </div>
                ) : (
                  folders.map(f => (
                    <button
                      key={f.id}
                      type="button"
                      className="folder-pick-item"
                      onClick={() => handleMoveSelectedToFolder(f.id)}
                      disabled={moveSubmitting}
                    >
                      <Folder size={18} />
                      <span className="folder-pick-item__name">{f.name}</span>
                      <span className="folder-pick-item__count">
                        {f.job_count}
                      </span>
                    </button>
                  ))
                )}
                <button
                  type="button"
                  className="folder-pick-item folder-pick-item--detach"
                  onClick={() => handleMoveSelectedToFolder(null)}
                  disabled={moveSubmitting}
                >
                  <X size={18} />
                  <span className="folder-pick-item__name">Be aplanko</span>
                </button>
              </div>
            </div>
            <div className="folder-modal__footer">
              <button
                type="button"
                className="btn btn--secondary"
                onClick={closeMoveModal}
                disabled={moveSubmitting}
              >
                Atšaukti
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobManagement;
